import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { markLoaded } from './States'

export type CardPadding = 'none' | 'sm' | 'md' | 'lg'

const CARD_PADDING: Record<CardPadding, string> = {
  none: '',
  sm: 'px-4 py-3',
  md: 'px-5 py-4',
  lg: 'px-6 py-5',
}

export interface CardProps {
  children: ReactNode
  className?: string
  /**
   * بدون سایه و با حاشیه ساده — اکنون همان حالت پیش‌فرض است.
   *
   * @deprecated بی‌اثر است. کارت از وقتی «سطح ۱» ارتفاع Stitch شد
   * (حاشیه ۱px و بدون سایه) همیشه همین حالت را دارد. فقط برای
   * نشکستنِ مصرف‌کننده‌های موجود مانده است.
   */
  flat?: boolean
  as?: 'div' | 'section' | 'article'
  /** برجسته‌سازی با Hover — «سطح ۲» در مقیاس ارتفاع Stitch */
  interactive?: boolean
}

/* ------------------------------------------------------------------
   کارت — سطحِ برجسته، نه جعبهٔ خط‌کشی‌شده
   ------------------------------------------------------------------
   🔴 کارت حالا گردیِ بیشتر (`radius-lg`) و سایهٔ ملایم دارد و حاشیه‌اش
   کم‌رنگ‌تر شده است.

   پیش‌تر «سطح ۱» بود: حاشیهٔ ۱px و هیچ سایه‌ای. وقتی کنترل‌های فرم به
   مدل Filled رفتند، زمینهٔ خاکستریِ فیلدها و حاشیهٔ خاکستریِ کارت دو
   خاکستریِ نزدیک به هم شدند و مرزِ کارت گم شد — کارت شبیه یک قابِ
   خط‌کشی دور چند فیلد دیده می‌شد و نه یک سطحِ مستقل.

   حالا عمق از **سایه** می‌آید و نه از خط: کارتِ سفید روی زمینهٔ
   خاکستریِ صفحه بلند می‌شود و فیلدهای خاکستری داخلش فرو می‌روند. همان
   سلسله‌مراتبی که مدل Filled لازم دارد.

   ⚠️ حاشیه کاملاً حذف نشد. در پوستهٔ تاریک سایه تقریباً دیده نمی‌شود
   (روی زمینهٔ تیره، سایهٔ تیره بی‌اثر است) و بدون حاشیه مرزِ کارت آنجا
   کاملاً از بین می‌رفت. پس حاشیه می‌ماند و در تاریکی همان نقش را
   بازی می‌کند که سایه در روشنایی.
   ------------------------------------------------------------------ */
export function Card({
  children,
  className,
  flat: _flat = false,
  interactive = false,
  as: Tag = 'div',
}: CardProps) {
  return (
    <Tag
      className={cn(
        /* ‎.pf-reveal‎ (tokens.css): کارتی که تازه رسیده — پس از بارگذاری یا با
           آمدنِ صفحه — نرم پدیدار می‌شود، نه یک‌باره (۱۴۰۵/۰۸/۱۳) */
        'pf-reveal rounded-[var(--pf-radius-lg)] border border-[var(--pf-border)] bg-[var(--pf-surface)]',
        'shadow-[var(--pf-shadow-sm)]',
        interactive &&
          'transition-shadow duration-[var(--pf-duration-fast)] hover:shadow-[var(--pf-shadow-lg)]',
        className,
      )}
    >
      {children}
    </Tag>
  )
}

export interface CardHeaderProps {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /**
   * نشانی تصویر سه‌بعدی — کنارِ عنوان می‌نشیند.
   * ⚠️ مثل PageHeader و StatCard نشانی کامل می‌گیرد و پکیج خودش مسیر
   * نمی‌سازد؛ ui-kit نباید رجیستری ماژول‌های اپ را بشناسد.
   */
  illustration?: string
  className?: string
}

export function CardHeader({
  title,
  description,
  actions,
  illustration,
  className,
}: CardHeaderProps) {
  return (
    <div
      className={cn(
        /*
          ⚠️ `flex-wrap` عمدی است.

          🔴 بدون آن، عنوان و اقدام‌ها در یک ردیفِ نشکستنی می‌مانند و
          چون ظرفِ اقدام‌ها `shrink-0` است، در عرض کم از لبهٔ کارت
          بیرون می‌زنند — نه جمع می‌شوند و نه به خط بعد می‌روند.
        */
        'flex flex-wrap items-start justify-between gap-4 border-b border-[var(--pf-border)] px-5 py-4',
        className,
      )}
    >
      {illustration && (
        <img
          src={illustration}
          alt=""
          aria-hidden="true"
          width={36}
          height={36}
          loading="lazy"
          decoding="async"
          onLoad={markLoaded}
          onError={markLoaded}
          className="pf-img size-9 shrink-0 object-contain"
        />
      )}
      {/*
        ⚠️ `basis` عمدی است و نه `flex-1` تنها.

        🔴 با `min-w-0 flex-1` عنوان تا هیچ جمع می‌شد و به‌جای رفتنِ
        اقدام‌ها به خط بعد، عنوان به یک کلمهٔ بریده تبدیل می‌شد. حالا
        عنوان زیر این عرض کوتاه نمی‌آید و ردیف می‌شکند.
      */}
      <div className="min-w-0 flex-1 basis-48">
        <h2 className="truncate text-[length:var(--pf-text-base)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]">
          {title}
        </h2>
        {description && (
          <p className="mt-0.5 text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}

/**
 * بدنه کارت.
 *
 * ⚠️ `padding` افزوده شد چون یک اندازه برای همه جواب نمی‌داد: کارتِ
 * فرمِ اصلیِ یک صفحه با ۷ فیلد، به فضای بیشتری از کارتِ فشرده‌ای که
 * کنارِ چند تای دیگر در شبکه می‌نشیند نیاز دارد. پیش‌تر هر صفحه با
 * `className` این را دستی بازنویسی می‌کرد — که یعنی دو کلاسِ `px`
 * متضاد روی یک عنصر و برنده‌شان ترتیبِ خروجی Tailwind.
 */
export function CardBody({
  children,
  className,
  padding = 'md',
}: {
  children: ReactNode
  className?: string
  padding?: CardPadding
}) {
  return <div className={cn(CARD_PADDING[padding], className)}>{children}</div>
}

/**
 * پانوشت کارت — جای دکمه‌های اقدامِ همان کارت.
 *
 * ⚠️ زمینهٔ ملایم می‌گیرد و نه سفید: پانوشت باید از بدنه جدا دیده شود
 * وگرنه دکمه‌ها شبیه ادامهٔ محتوا می‌نشینند. `--pf-surface-muted` همان
 * خاکستریِ کنترل‌های Filled است، پس نوارِ اقدام با فیلدهای بالایش
 * یک خانواده می‌ماند.
 *
 * 🔴 گوشه‌های پایین باید گرد باشد. کارت `radius-lg` است و پانوشتِ
 * زمینه‌دارِ گوشه‌تیز از دو گوشهٔ پایینِ کارت بیرون می‌زد — یک مثلث
 * رنگی بیرونِ لبهٔ گرد.
 */
export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-[var(--pf-space-3)]',
        'border-t border-[var(--pf-border)] bg-[var(--pf-surface-muted)] px-5 py-3',
        'rounded-b-[calc(var(--pf-radius-lg)-1px)]',
        className,
      )}
    >
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------
   سرصفحه صفحه — عنوان اصلی صفحه
   ------------------------------------------------------------------
   پیش از این هر صفحه `<header>` خودش را دستی می‌ساخت و نتیجه‌اش
   واگرایی بود: بعضی `items-center` بودند بعضی `items-start`، بعضی
   `text-fg-muted` و بعضی `text-text-muted`. اینجا یک‌جا جمع شده است.

   ⚠️ عمداً آیکون ندارد. آیکون وقتی ارزش دارد که به *پیدا کردن* یا
   *تفکیک* کمک کند؛ ولی کاربری که سرصفحه را می‌بیند همین حالا داخل
   همان صفحه است و عنوان ۲۴px این کار را کرده. آیکون کنارش فقط همان
   را تکرار می‌کرد. رندرهای سه‌بعدی جایشان لانچر (۵۶px) و حالت خالی
   (۸۸px) است — جایی که واقعاً بزرگ دیده می‌شوند.
   ------------------------------------------------------------------ */

export interface PageHeaderProps {
  title: ReactNode
  description?: ReactNode
  /**
   * ردیف زیر عنوان برای قرص وضعیت و نشانه‌های کوتاه.
   * جدا از `description` است چون آن یک پاراگراف متنی است و این یک
   * ردیف افقی از عنصرها.
   */
  meta?: ReactNode
  actions?: ReactNode
  /**
   * نشانی آیکون سه‌بعدی صفحه — در ۵۶px کنار عنوان.
   *
   * ⚠️ مثل EmptyState و StatCard نشانی کامل می‌گیرد و پکیج خودش مسیر
   * نمی‌سازد؛ ui-kit نباید رجیستری ماژول‌های اپ را بشناسد.
  */
  illustration?: string
  className?: string
}

export function PageHeader({
  title,
  description,
  meta,
  actions,
  illustration,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <div className="flex min-w-0 items-center gap-3">
        {illustration && (
          <img
            src={illustration}
            alt=""
            aria-hidden="true"
            width={56}
            height={56}
            /* بالای تاشو است و بلافاصله دیده می‌شود؛ lazy کردنش فقط
               پرشِ چیدمان می‌سازد. */
            decoding="async"
            onLoad={markLoaded}
            onError={markLoaded}
            className="pf-img size-11 shrink-0 object-contain sm:size-14"
          />
        )}
        <div className="min-w-0">
          <h1 className="text-[length:var(--pf-text-2xl)] font-[var(--pf-weight-bold)] text-[var(--pf-text)]">
            {title}
          </h1>
          {description && (
            <p className="mt-0.5 text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">
              {description}
            </p>
          )}
          {meta && <div className="mt-1.5 flex flex-wrap items-center gap-2">{meta}</div>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  )
}

/* ------------------------------------------------------------------
   سرصفحه بخش — عنوان کوچک + خط راهنما تا انتهای عرض
   ------------------------------------------------------------------
   الگوی Nova v2 برای جدا کردن بخش‌های یک صفحه بدون افزودن قاب:
   عنوان ۱۳px و بعد یک خط ۱px که فضای باقی‌مانده را پر می‌کند.
   ------------------------------------------------------------------ */

export interface SectionHeaderProps {
  title: ReactNode
  /** شناسه برای aria-labelledby بخش دربرگیرنده */
  id?: string
  /** اکشن اختیاری در انتهای خط — مثل لینک «همه» */
  actions?: ReactNode
  className?: string
}

export function SectionHeader({ title, id, actions, className }: SectionHeaderProps) {
  return (
    <div className={cn('mb-4 flex items-center gap-3', className)}>
      <h2
        id={id}
        className="shrink-0 text-[length:var(--pf-text-sm)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]"
      >
        {title}
      </h2>
      <div aria-hidden="true" className="h-px flex-1 bg-[var(--pf-border)]" />
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------
   کارت آماری — ویجت داشبورد بند ۱۱.۲ و ۱۱.۳
   ------------------------------------------------------------------ */

export interface StatCardProps {
  label: string
  value: ReactNode
  hint?: ReactNode
  icon?: ReactNode
  /**
   * نشانی تصویر سه‌بعدی — جایگزین `icon` و قرصِ رنگی می‌شود.
   * ⚠️ مثل EmptyState نشانی می‌گیرد و خودش مسیر نمی‌سازد.
   */
  illustration?: string
  tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger'
  href?: string
  onClick?: () => void
  className?: string
}

const STAT_TONES = {
  neutral: 'bg-[var(--pf-surface-muted)] text-[var(--pf-text-muted)]',
  brand: 'bg-[var(--pf-brand-50)] text-[var(--pf-brand-700)]',
  success: 'bg-[var(--pf-success-50)] text-[var(--pf-success-700)]',
  warning: 'bg-[var(--pf-warning-50)] text-[var(--pf-warning-700)]',
  danger: 'bg-[var(--pf-danger-50)] text-[var(--pf-danger-700)]',
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  illustration,
  tone = 'neutral',
  href,
  onClick,
  className,
}: StatCardProps) {
  const interactive = !!href || !!onClick
  const Tag = href ? 'a' : onClick ? 'button' : 'div'

  return (
    <Tag
      {...(href ? { href } : {})}
      {...(onClick ? { onClick, type: 'button' as const } : {})}
      className={cn(
        'pf-reveal flex items-center gap-3.5 rounded-[var(--pf-radius-lg)] border border-[var(--pf-border)]',
        'bg-[var(--pf-surface)] px-4 py-3.5 text-start shadow-[var(--pf-shadow-xs)]',
        interactive &&
          'transition-colors duration-[var(--pf-duration-fast)] hover:bg-[var(--pf-surface-hover)] cursor-pointer',
        className,
      )}
    >
      {/*
        ⚠️ تصویر سه‌بعدی عمداً قرصِ رنگی نمی‌گیرد.
        قرص برای آیکون خطیِ تک‌رنگ ساخته شده که رنگش را از `tone`
        می‌گیرد؛ رندر سه‌بعدیِ تمام‌رنگ روی پس‌زمینه سبز یا کهربایی
        وصله ناجور می‌شود — آن هم وقتی tone با تغییر وضعیت عوض می‌شود
        و تصویر ثابت می‌ماند.
      */}
      {illustration ? (
        <img
          src={illustration}
          alt=""
          aria-hidden="true"
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          onLoad={markLoaded}
          onError={markLoaded}
          className="pf-img size-10 shrink-0 object-contain"
        />
      ) : (
        icon && (
          <span
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-[var(--pf-radius-md)]',
              STAT_TONES[tone],
            )}
          >
            {icon}
          </span>
        )
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
          {label}
        </span>
        <span className="block truncate text-[length:var(--pf-text-xl)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)] pf-tnum leading-tight">
          {value}
        </span>
        {hint && (
          <span className="block truncate text-[length:var(--pf-text-2xs)] text-[var(--pf-text-subtle)]">
            {hint}
          </span>
        )}
      </span>
    </Tag>
  )
}
